import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Avatar } from '../../components/ui/Avatar';
import { LinkButton } from '../../components/ui/Button';
import { Chip } from '../../components/ui/Card';
import { copy } from '../../config/copy';
import { useDrawerState } from '../../app/DrawerLink';
import { BadgeShelf } from '../achievements/BadgeShelf';
import { gradientCss, type BannerGradient } from './banner';
import { AlbumList } from '../albums/AlbumList';
import { PostList } from '../posts/PostList';
import { MessageButton } from '../messages/MessagesPage';
import { TrackList } from '../tracks/TrackList';
import { ApiError, api } from '../../lib/api';
import { LiveSection } from '../rooms/LiveSection';
import { ProfileLinkRow } from './links';
import { useSession } from '../../stores/session';
import { TipButton } from '../payments/TipButton';
import { BlockButton } from './BlockButton';
import { FollowButton } from './FollowButton';
import type { Profile } from './types';

/**
 * A public profile at `/{username}`, following the profile mockup in dev/design.
 *
 * The bare-username route is deliberate - the shape people expect, and the one
 * that reads well when shared. It works because two things hold together:
 * React Router matches static segments before dynamic ones, so `/login` and
 * `/admin` win; and the server refuses to register those names at all
 * (`RegisterAction::RESERVED_USERNAMES`). Routing alone would let somebody
 * claim `settings` and shadow nothing until that page shipped.
 */
export function ProfilePage() {
  // Above the early returns: a hook cannot run conditionally.
  const drawerState = useDrawerState();
  const { username = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const me = useSession((s) => s.user);
  const { data, error, isPending } = useQuery({
    queryKey: ['profile', username],
    queryFn: () => api.get<Profile>(`/users/${encodeURIComponent(username)}`),
    retry: false,
  });

  if (isPending) {
    return <div className="mx-auto max-w-[1500px] px-6 py-16 text-soft">Loading…</div>;
  }

  if (error) {
    const missing = error instanceof ApiError && error.status === 404;

    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <Alert tone={missing ? 'info' : 'error'}>
          {missing ? `Nobody here goes by @${username}.` : 'Could not load that profile.'}
        </Alert>
      </div>
    );
  }

  const isMe = me?.id === data.id;

  return (
    <div>
      {/*
        A broadcast has no page of its own - it is this person, doing something,
        right now. Above the banner because when somebody is on air that is the
        reason anybody is here; renders nothing at all otherwise, which is
        almost always for almost everybody.
      */}
      {/*
        Where Stripe sends somebody back to after they pay.
 
        Shown from the URL rather than from any state, because the round trip
        goes out to Stripe's domain and back: nothing in this tab survives it.
        `ThankYou` clears the parameter once seen, so a refresh or a shared link
        does not congratulate somebody for a payment they did not make.
      */}
      {params.get('tip') === 'thanks' && (
        <ThankYou
          name={data.display_name || data.username}
          onSeen={() => {
            const next = new URLSearchParams(params);

            next.delete('tip');
            setParams(next, { replace: true });
          }}
        />
      )}

      <LiveSection
        username={data.username}
        displayName={data.display_name || data.username}
        banner={data.banner}
        gradient={data.banner_gradient}
        canBeTipped={data.can_be_tipped}
      />

      <Banner url={data.banner} gradient={data.banner_gradient} />

      <div className="mx-auto max-w-[1500px] px-6">
        {/*
          The avatar overlaps the banner, which is why this block is pulled up
          and the banner is not clipped. Negative margin rather than absolute
          positioning so the header below still occupies real space and nothing
          has to be nudged when the bio wraps to a second line.
        */}
        <div className="relative z-10 -mt-14 flex flex-wrap items-end gap-5">
          {/*
            `relative z-10` is load-bearing, not tidying. Without it the banner
            paints over the pulled-up avatar and only its bottom half shows -
            which looks like a broken image rather than a stacking problem.
            The banner is `relative` for its own overflow clipping, and that is
            enough to win against a static sibling.
          */}
          <div data-profile-avatar className="rounded-full ring-4 ring-app">
            <Avatar src={data.avatar?.large} name={data.display_name} size={112} />
          </div>
        </div>

        <header className="mt-5 flex flex-wrap items-start gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-[2rem] font-bold leading-tight tracking-tight">{data.display_name}</h1>

              {data.verified && <Chip tone="signal">verified</Chip>}
              {isNew(data.created_at) && <Chip tone="fresh">{copy['label.newHere']}</Chip>}
            </div>

            {/*
              The metadata line is mono, and that is a real part of how this
              design reads: handle, place and tags scan as data rather than as
              prose sitting under a name.
            */}
            <div className="meta mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-faint">
              <span className="text-accent">@{data.username}</span>
              {data.location && (
                <>
                  <span aria-hidden>|</span>
                  <span>{data.location}</span>
                </>
              )}
              {/* Rehomed from the About tab, which no longer exists. It is one
                  short fact and it belongs with the other short facts, not
                  behind a click. */}
              <span aria-hidden>|</span>
              <span>joined {formatJoined(data.created_at)}</span>
            </div>

            {data.bio && (
              /*
                Rendered as text, never as markup. React escapes by default,
                which is what makes a bio safe - the moment somebody reaches for
                dangerouslySetInnerHTML here, this becomes stored XSS on the
                most visited page in the app.
              */
              <p className="mt-4 max-w-2xl whitespace-pre-wrap text-[15px] leading-relaxed text-text-2">
                {data.bio}
              </p>
            )}

            {/*
              Under the bio, and the only place they appear.
              They were in two others - a labelled pill row on the About tab and
              an "elsewhere" card in the sidebar - which meant a profile with
              five links rendered fifteen anchors and none of them were where
              people look. Here they are above the fold and on every tab.
            */}
            <ProfileLinkRow links={data.links} />
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {/*
              Editing opens in the drawer, so the profile it edits stays on
              screen behind it. `useDrawerState` rather than wrapping this in a
              `DrawerLink`, which would nest an anchor inside an anchor.
            */}
            {isMe ? (
              <LinkButton to="/settings" state={drawerState} variant="secondary">
                Edit profile
              </LinkButton>
            ) : (
              <>
                {/*
                  Follow and Message disappear once blocked - the server refuses
                  both, and a button that fails when pressed is worse than one
                  that is not there. Unblock is then the only thing on offer,
                  which also makes the state legible without a banner.
                */}
                {!data.is_blocked && (
                  <>
                    <FollowButton profile={data} />
                    {/*
                      Not a link to `/messages/{username}` - the route takes a
                      conversation id, and there may not be a conversation yet.
                      The button creates or finds one and goes to it.
                    */}
                    <MessageButton username={data.username} />

                    {/*
                      Tipping does not depend on a broadcast.
 
                      The live banner has its own tip button, but that one only
                      exists while somebody is on air - and the person who
                      remembers a set they enjoyed usually turns up afterwards.
                      Inside the `is_blocked` guard with the others, because the
                      server refuses a tip between blocked people and a button
                      that 403s is worse than no button.
                    */}
                    {data.can_be_tipped && (
                      <TipButton
                        username={data.username}
                        displayName={data.display_name || data.username}
                      />
                    )}
                  </>
                )}

                <BlockButton profile={data} />
              </>
            )}
          </div>
        </header>

        <StatStrip profile={data} />

        {/*
          Under the numbers, above the music.

          The figures say how much; the badges say what for. Putting them the
          other way round would push the tracks - which are the reason anybody
          came - further down for a row of pictures.
        */}
        <BadgeShelf username={data.username} />

        {/*
          Two columns, not tabs.

          A profile has exactly two kinds of thing on it, and hiding one behind
          a click bought nothing: albums are how an artist wants their work read
          and they were invisible unless you went looking. Side by side, both
          are simply there.

          Even halves. Tracks used to take a wide column and albums a narrow
          one, on the reasoning that a list of rows needs more room than a grid
          of covers - which was true until updates arrived and wanted a column
          of their own. Two equal halves give the covers room to sit three
          across and stop the updates column reading as a sidebar.

          Below `lg` they stack, tracks first.

          The About tab went with them. It held a bio already printed above it,
          a link row now under that bio, and a join date - all of which are on
          this page without it.
        */}
        {/*
          Two columns that flow independently, not one grid of three sections.

          They were siblings in a two-column grid, which auto-placed tracks and
          updates on row one and albums on row two - so row one was as tall as
          whichever side was taller, and every update somebody posted pushed
          albums further down behind a widening gap.

          A column that owns its own children cannot do that: albums sit under
          tracks whatever updates does.
        */}
        <div className="mt-8 grid items-start gap-10 lg:grid-cols-2">
          <div className="flex flex-col gap-10">
            <section aria-labelledby="profile-tracks">
              <h2 id="profile-tracks" className="border-b border-line pb-3 text-lg font-bold tracking-tight">
                {copy['noun.track.plural']}
              </h2>

              <TrackList username={data.username} isOwner={isMe} />
            </section>

            <section aria-labelledby="profile-albums">
              <h2 id="profile-albums" className="border-b border-line pb-3 text-lg font-bold tracking-tight">
                {copy['noun.collection.plural']}
              </h2>

              {/* Still three a row, but reached at a larger window than the
                  full-width grid needs - see `compact`. */}
              <AlbumList username={data.username} isOwner={isMe} compact />
            </section>
          </div>

          {/*
            The right-hand column.

            Updates are what changes between releases, so they are the reason to
            come back to a profile that has not put anything out lately - worth
            a column rather than a footnote under the music.

            `PostList` decides whether it draws anything: the endpoint is behind
            a flag and answers 404 when it is off, which is also the answer a
            signed-out reader gets for a profile that has nothing to show. The
            flag is deliberately not re-checked here - it is not on the session
            for a signed-out reader, and gating on it hid public updates from
            exactly the people they are public for.
          */}
          <PostList username={data.username} isOwner={isMe} />

        </div>
      </div>
    </div>
  );
}

/**
 * The header: an uploaded image, or the gradient underneath it.
 *
 * ## There is no third state
 *
 * This used to fall back to a diagonal hatch, and since nothing in the product
 * could set `banner_key`, that hatch was what every profile on the site showed.
 * A wall of identical placeholders is harder to scan than a wall of distinct
 * colours - the same argument `Avatar` makes for initials over grey
 * silhouettes, on the largest surface of the page rather than the smallest.
 *
 * `BannerPresets::resolve()` derives a gradient from the username when nobody
 * has chosen one, so this always has something real to paint.
 *
 * ## A fixed height rather than an aspect ratio
 *
 * So the avatar's overlap does not move between somebody who has uploaded an
 * image and somebody who has not. 2400x560 is the size the design specifies and
 * the size `ProcessBanner` writes.
 */
function Banner({ url, gradient }: { url: string | null; gradient: BannerGradient }) {
  return (
    <div
      data-profile-banner={url ? 'image' : gradient.preset}
      /*
       * Height scales with width, within bounds.
       *
       * It was a flat 280px, which meant the container's aspect ratio was
       * whatever the viewport happened to be - 6.9:1 on a 1920 monitor, wider
       * still on anything larger. `object-cover` then filled the width and ate
       * the top and bottom of the image, so the recommended 2400x560 (4.3:1)
       * was cropped hardest by the people with the biggest screens.
       *
       * 20vw holds the ratio near 5:1 across ordinary widths, so the same
       * upload looks the same everywhere. The floor keeps it a banner on a
       * phone; the ceiling stops it eating an ultrawide monitor, which is the
       * one case where the ratio still drifts.
       */
      className="relative w-full overflow-hidden border-b border-line"
      style={{
        height: 'clamp(180px, 20vw, 420px)',
        // Painted even when there is an image over it: it is what shows while
        // a large header decodes, better than a flash of empty panel.
        background: gradientCss(gradient),
      }}
    >
      {url && <img src={url} alt="" className="h-full w-full object-cover" />}
    </div>
  );
}

/**
 * The five-column stat strip.
 *
 * Numbers in Space Grotesk at size, labels in mono underneath - the pairing the
 * design uses everywhere it presents figures, and the reason a row of five
 * reads as a table rather than as five unrelated facts.
 */
function StatStrip({ profile }: { profile: Profile }) {
  const stats: { value: string; label: string; to?: string; tone?: string }[] = [
    {
      value: profile.follower_count.toLocaleString(),
      label: copy['noun.follower.plural'],
      to: `/${profile.username}/followers`,
    },
    { value: profile.following_count.toLocaleString(), label: 'following', to: `/${profile.username}/following` },
    { value: profile.plays_this_week.toLocaleString(), label: 'plays this week' },
    { value: profile.track_count.toLocaleString(), label: copy['noun.track.plural'].toLowerCase() },
    // Not "rooms played in": a broadcast is a person, and what matters to an
    // artist is that their music was on one.
    { value: profile.played_live.toLocaleString(), label: 'played live' },
  ];

  return (
    <div className="mt-7 grid grid-cols-2 overflow-hidden rounded-[var(--radius-card)] border border-line sm:grid-cols-3 lg:grid-cols-5">
      {stats.map((stat, index) => {
        const body = (
          <>
            <div className={`text-2xl font-bold ${stat.tone ?? ''}`}>{stat.value}</div>
            <div className="meta mt-1 text-faint">{stat.label}</div>
          </>
        );

        return stat.to ? (
          <Link
            key={stat.label}
            to={stat.to}
            className={`px-5 py-4 transition-colors hover:bg-raised ${index > 0 ? 'border-l border-line' : ''}`}
          >
            {body}
          </Link>
        ) : (
          <div key={stat.label} className={`px-5 py-4 ${index > 0 ? 'border-l border-line' : ''}`}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

/*
 * No rooms tab.
 *
 * A broadcast belongs to a person rather than being a venue they own, so
 * "their rooms" is a list of exactly one thing - and that thing is this page,
 * which grows a live section at the top when they are on air.
 */



/** Within a fortnight of joining. Drives the "new here" chip. */
function isNew(createdAt: string): boolean {
  const joined = Date.parse(createdAt.replace(' ', 'T'));

  return Number.isFinite(joined) && Date.now() - joined < 14 * 24 * 60 * 60 * 1000;
}

function formatJoined(createdAt: string): string {
  const joined = new Date(createdAt.replace(' ', 'T'));

  return Number.isNaN(joined.getTime())
    ? createdAt
    : joined.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/**
 * "Thanks, that went through."
 *
 * Deliberately not a permanent banner: the payment is done, the ledger has it,
 * and the host has been told. This exists so the person who just paid is not
 * dropped back on a page that looks exactly as it did before they left, which
 * is the moment people wonder whether it worked and pay twice.
 *
 * Clears itself from the URL after a moment so that a bookmark or a shared link
 * carrying `?tip=thanks` does not congratulate a stranger.
 */
function ThankYou({ name, onSeen }: { name: string; onSeen: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onSeen, 8000);

    return () => clearTimeout(timer);
  }, [onSeen]);

  return (
    /*
      A floating pill, not a banner.
 
      It was a full-width `Alert` above the live section, which is full-bleed
      and dark - so the bar sat across the top of the page looking like a
      browser warning rather than a thank-you, and pushed the broadcast down to
      make room for itself.
 
      Fixed and centred at the bottom instead: it overlays rather than displaces,
      it is nowhere near a control, and it leaves on its own.
    */
    <div
      className="pointer-events-none fixed bottom-[calc(var(--spacing-dock)+20px)] left-1/2 z-40 -translate-x-1/2"
      data-tip-thanks
    >
      <div className="flex items-center gap-2.5 rounded-[var(--radius-pill)] border border-fresh/40 bg-raised px-4 py-2.5 shadow-2xl">
        <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0 text-fresh" fill="currentColor" aria-hidden>
          <path d="M6.2 11.3 3.4 8.5l-1 1L6.2 13.3 14 5.5l-1-1z" />
        </svg>
        <span className="whitespace-nowrap text-sm text-ink">
          Thanks — your tip is on its way to {name}.
        </span>
      </div>
    </div>
  );
}
